import React, { useState, useEffect, useRef } from 'react';
import { 
  CloudArrowUpIcon, 
  CheckCircleIcon, 
  CpuChipIcon,
  LightBulbIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface EnhancedAnalysisLoadingProps {
  currentStage: 'uploading' | 'analyzing' | 'processing' | 'complete';
  apiProvider: string;
  progress?: number;
  estimatedTime?: number;
  onComplete?: () => void;
}

interface LoadingStage {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  estimatedDuration: number;
  color: string;
  particles: { count: number; color: string };
}

const EnhancedAnalysisLoading: React.FC<EnhancedAnalysisLoadingProps> = ({
  currentStage,
  apiProvider,
  progress,
  onComplete
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [pulseIntensity, setPulseIntensity] = useState(1);
  const [neuronsActive, setNeuronsActive] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const aiTips = [
    "🧠 Neural networks are analyzing visual patterns and features in your image...",
    "🔍 AI is comparing your item against millions of market data points...",
    "💎 Machine learning models are evaluating condition, rarity, and demand...",
    "📊 Advanced algorithms are calculating real-time market valuations...",
    "🎯 Computer vision is identifying brand markers and unique characteristics...",
    "⚡ Deep learning is processing complex visual and contextual information...",
    "🌐 AI is cross-referencing multiple marketplace databases...",
    "🔮 Predictive models are forecasting future value trends..."
  ];

  const stages: LoadingStage[] = [
    {
      id: 'uploading',
      title: 'Uploading Image',
      icon: CloudArrowUpIcon,
      description: 'Securely transferring your image to AI processing centers',
      estimatedDuration: 3,
      color: 'from-blue-400 to-blue-600',
      particles: { count: 20, color: '#3B82F6' }
    },
    {
      id: 'analyzing',
      title: `${apiProvider} AI Analysis`,
      icon: CpuChipIcon,
      description: 'Neural networks are processing visual data and extracting insights',
      estimatedDuration: 45,
      color: 'from-purple-400 to-pink-600',
      particles: { count: 50, color: '#8B5CF6' }
    },
    {
      id: 'processing',
      title: 'Generating Insights',
      icon: ChartBarIcon,
      description: 'Compiling market data and creating valuation report',
      estimatedDuration: 5,
      color: 'from-green-400 to-emerald-600',
      particles: { count: 30, color: '#10B981' }
    },
    {
      id: 'complete',
      title: 'Analysis Complete',
      icon: CheckCircleIcon,
      description: 'Ready to display your personalized insights!',
      estimatedDuration: 1,
      color: 'from-green-500 to-green-700',
      particles: { count: 60, color: '#22C55E' }
    }
  ];

  // Particle system for background animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 400;
    canvas.height = 300;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
    }> = [];

    const stage = stages[currentStageIndex];
    const particleCount = stage?.particles.count || 20;

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        life: Math.random() * 100,
        maxLife: 100,
        size: Math.random() * 3 + 1
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life -= 0.5;

        // Reset particle if it dies
        if (particle.life <= 0) {
          particle.x = Math.random() * canvas.width;
          particle.y = Math.random() * canvas.height;
          particle.life = particle.maxLife;
        }

        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Draw particle
        const alpha = particle.life / particle.maxLife;
        ctx.globalAlpha = alpha * 0.6;
        ctx.fillStyle = stage?.particles.color || '#3B82F6';
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();

        // Draw connections
        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const distance = Math.sqrt(
              Math.pow(particle.x - otherParticle.x, 2) +
              Math.pow(particle.y - otherParticle.y, 2)
            );

            if (distance < 100) {
              ctx.globalAlpha = (1 - distance / 100) * 0.2;
              ctx.strokeStyle = stage?.particles.color || '#3B82F6';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.stroke();
            }
          }
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentStageIndex]);

  useEffect(() => {
    const stageIndex = stages.findIndex(stage => stage.id === currentStage);
    setCurrentStageIndex(stageIndex);
    setNeuronsActive(currentStage === 'analyzing');
  }, [currentStage]);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 0.1);
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const tipTimer = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % aiTips.length);
    }, 4000);

    return () => clearInterval(tipTimer);
  }, [aiTips.length]);

  useEffect(() => {
    const pulseTimer = setInterval(() => {
      setPulseIntensity(prev => prev === 1 ? 1.2 : 1);
    }, 1000);

    return () => clearInterval(pulseTimer);
  }, []);

  useEffect(() => {
    if (currentStage === 'complete' && onComplete) {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStage, onComplete]);

  const calculateProgress = () => {
    if (progress !== undefined) return progress;
    
    const completedStages = currentStageIndex;
    const totalStages = stages.length;
    const baseProgress = (completedStages / totalStages) * 100;
    
    const currentStageProgress = Math.min(
      (elapsedTime / (stages[currentStageIndex]?.estimatedDuration || 1)) * 
      (100 / totalStages), 
      100 / totalStages
    );
    
    return Math.min(baseProgress + currentStageProgress, 100);
  };

  const formatTime = (seconds: number) => {
    return `${Math.floor(seconds)}s`;
  };

  const currentStageData = stages[currentStageIndex];

  return (
    <div className="max-w-4xl mx-auto text-center py-8 px-4">
      {/* Background Canvas Animation */}
      <div className="relative mb-8">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 mx-auto opacity-30 dark:opacity-20"
          style={{ 
            maxWidth: '100%', 
            height: 'auto',
            filter: 'blur(0.5px)'
          }}
        />
        
        {/* Main Loading Icon */}
        <div className="relative z-10">
          <div 
            className={`relative inline-flex items-center justify-center w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r ${currentStageData?.color || 'from-blue-400 to-blue-600'} shadow-2xl`}
            style={{ 
              transform: `scale(${pulseIntensity})`,
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            {/* Rotating rings */}
            <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-spin" style={{ animationDuration: '3s' }}></div>
            <div className="absolute inset-2 rounded-full border-2 border-white/20 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
            
            {/* Central Icon */}
            {currentStageData?.icon && (
              <currentStageData.icon className="h-10 w-10 text-white drop-shadow-lg" />
            )}
            
            {/* Neural network overlay for AI analysis */}
            {neuronsActive && (
              <div className="absolute inset-0 rounded-full">
                <div className="absolute top-2 left-2 w-2 h-2 bg-white rounded-full animate-ping"></div>
                <div className="absolute top-4 right-3 w-1 h-1 bg-white rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute bottom-3 left-4 w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                <div className="absolute bottom-2 right-2 w-1 h-1 bg-white rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Title and Description */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 animate-pulse">
          {currentStageData?.title || 'Processing...'}
        </h2>
        
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
          {currentStageData?.description || 'Please wait while we process your request...'}
        </p>

        {/* Enhanced Progress Bar */}
        <div className="relative w-full max-w-md mx-auto mb-6">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden shadow-inner">
            <div 
              className={`h-full bg-gradient-to-r ${currentStageData?.color || 'from-blue-400 to-blue-600'} rounded-full transition-all duration-500 ease-out relative`}
              style={{ width: `${calculateProgress()}%` }}
            >
              {/* Animated shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
              
              {/* Moving highlight */}
              <div 
                className="absolute top-0 left-0 h-full w-8 bg-white/20 animate-pulse"
                style={{ 
                  animation: 'slide 2s infinite',
                  animationDirection: 'alternate'
                }}
              ></div>
            </div>
          </div>
          
          <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 mt-2">
            <span>{Math.round(calculateProgress())}% complete</span>
            <span>⏱️ {formatTime(elapsedTime)}</span>
          </div>
        </div>
      </div>

      {/* AI Tips with Smooth Transitions */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 mb-8 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <LightBulbIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-1 animate-pulse" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
              AI Processing Insight
            </h3>
            <p 
              key={currentTipIndex}
              className="text-sm text-blue-700 dark:text-blue-300 animate-fade-in"
            >
              {aiTips[currentTipIndex]}
            </p>
          </div>
        </div>
      </div>

      {/* Stage Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stages.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isCurrent = index === currentStageIndex;
          const IconComponent = stage.icon;
          
          return (
            <div
              key={stage.id}
              className={`p-4 rounded-xl border-2 transition-all duration-500 ${
                isCompleted
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 shadow-lg'
                  : isCurrent
                  ? 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-300 dark:border-blue-700 shadow-xl animate-pulse'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <IconComponent 
                className={`h-8 w-8 mx-auto mb-3 transition-all duration-300 ${
                  isCompleted
                    ? 'text-green-600 dark:text-green-400'
                    : isCurrent
                    ? 'text-blue-600 dark:text-blue-400 animate-bounce'
                    : 'text-gray-400 dark:text-gray-600'
                }`} 
              />
              <p className={`text-sm font-medium text-center ${
                isCompleted
                  ? 'text-green-700 dark:text-green-300'
                  : isCurrent
                  ? 'text-blue-700 dark:text-blue-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {stage.title}
              </p>
              
              {isCompleted && (
                <div className="mt-2 flex justify-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnhancedAnalysisLoading;
